/**
 * Gluon API client for the Mobile Home Layout editor.
 *
 * Talks to `/v1/gluon/mobile-home-layout`. Governance: edits go to a DRAFT
 * (PUT); the mobile app is only ever served the PUBLISHED layout. `publish`
 * promotes the draft, archiving the prior published version for `rollback`.
 * Layouts resolve application → plan → global → in-code default.
 */

import axios from 'axios'
import { ClientApplication } from '@numeo/types-core'
import { HomeLayoutResponse, HomeScreenId, MobileHomeLayoutScope, MobileHomeSegment } from '@numeo/types'

/**
 * The server's own error message, not axios's generic one.
 *
 * The shared response interceptor re-rejects with the raw `AxiosError`, whose
 * `.message` is always "Request failed with status code 400" — the useful text
 * (`"Invalid layout: sections.0.props.linkUrl: must be an https url"`, or
 * "Image too large. Maximum size is 5MB.") sits on `response.data.message` and
 * is otherwise dropped. Without this the field-level validation on the server,
 * and the `describeValidationError` work behind it, never reaches an operator.
 */
export { apiErrorMessage } from './errorMessage'

const DEFAULT_SCREEN: HomeScreenId = 'dispatcher.home'

interface Envelope<T> {
    success: boolean
    data: T
    message?: string
}

interface ApplicationsListResponse {
    applications: ClientApplication[]
    total: number
    totalPages: number
}

interface HomeLayoutHistoryEntry {
    index: number
    publishedAt: string
    publishedBy: string
}

export interface HomeLayoutGetResponse {
    /** The live layout the mobile app is currently served. */
    published: HomeLayoutResponse
    /** The working copy the editor mutates (draft if present, else published/default). */
    draft: HomeLayoutResponse
    /** True when unpublished draft edits exist. */
    hasDraft: boolean
    /** True when nothing is published yet (the in-code default is live). */
    isDefault: boolean
    /** The stored audience, for `segment` scope only. */
    segment?: MobileHomeSegment
    approvedBy?: string
    updatedAt?: string
    updatedBy?: string
    publishedAt?: string
    publishedBy?: string
    history: HomeLayoutHistoryEntry[]
}

/** Scope + its keying field — sent as the body of every mutating call. */
type LayoutKey = { screen: HomeScreenId; scope: MobileHomeLayoutScope; projectName?: string; planTier?: string; segmentId?: string }

/** Options common to every call: the target screen + the scope's keying field. */
interface KeyOpts {
    screen?: HomeScreenId
    projectName?: string
    planTier?: string
    segmentId?: string
}

const keyBody = (scope: MobileHomeLayoutScope, opts?: KeyOpts): LayoutKey => ({
    screen: opts?.screen ?? DEFAULT_SCREEN,
    scope,
    projectName: scope === 'application' ? opts?.projectName : undefined,
    planTier: scope === 'plan' ? opts?.planTier : undefined,
    segmentId: scope === 'segment' ? opts?.segmentId : undefined,
})

export const mobileHomeLayoutApi = {
    /** Search applications by projectName (substring) — reused from the features page UX. */
    searchApplications: async (projectName: string): Promise<ClientApplication[]> => {
        const params = new URLSearchParams()
        if (projectName) params.append('projectName', projectName)
        params.append('limit', '20')
        const res = await axios.get<ApplicationsListResponse>(`/gluon/applications?${params.toString()}`)
        return res.data?.applications ?? []
    },

    /** Fetch published + draft + history for a scope + screen. */
    /**
     * Upload campaign artwork; resolves to the PUBLIC url to store on the section.
     * The url is permanent and unauthenticated by design — it is embedded in a
     * layout the mobile client caches for offline use, so a signed url would
     * break the banner as soon as it expired.
     */
    uploadPromoImage: async (file: File): Promise<string> => {
        const form = new FormData()
        form.append('file', file)
        const res = await axios.post<Envelope<{ url: string }>>('/gluon/mobile-assets/promo-image', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        return res.data.data.url
    },

    get: async (scope: MobileHomeLayoutScope, opts?: KeyOpts): Promise<HomeLayoutGetResponse> => {
        const params = new URLSearchParams({ scope, screen: opts?.screen ?? DEFAULT_SCREEN })
        if (scope === 'application' && opts?.projectName) params.append('projectName', opts.projectName)
        if (scope === 'plan' && opts?.planTier) params.append('planTier', opts.planTier)
        if (scope === 'segment' && opts?.segmentId) params.append('segmentId', opts.segmentId)
        const res = await axios.get<Envelope<HomeLayoutGetResponse>>(`/gluon/mobile-home-layout?${params.toString()}`)
        return res.data.data
    },

    /** Save the working draft (does NOT change what mobile is served).
     *  `segment` is the audience, required for `segment` scope — it is drafted
     *  alongside the layout so re-aiming a live layout is itself a publish. */
    saveDraft: async (scope: MobileHomeLayoutScope, layout: HomeLayoutResponse, opts?: KeyOpts, segment?: MobileHomeSegment): Promise<void> => {
        await axios.put<Envelope<{ draft: HomeLayoutResponse }>>(`/gluon/mobile-home-layout`, { ...keyBody(scope, opts), layout, segment })
    },

    /** Stamp advisory approval on the current draft. */
    approve: async (scope: MobileHomeLayoutScope, opts?: KeyOpts): Promise<void> => {
        await axios.post<Envelope<unknown>>(`/gluon/mobile-home-layout/approve`, keyBody(scope, opts))
    },

    /** Promote the draft to published (goes live to users). */
    publish: async (scope: MobileHomeLayoutScope, opts?: KeyOpts): Promise<void> => {
        await axios.post<Envelope<{ published: HomeLayoutResponse }>>(`/gluon/mobile-home-layout/publish`, keyBody(scope, opts))
    },

    /** Restore a past published version by index. */
    rollback: async (scope: MobileHomeLayoutScope, versionIndex: number, opts?: KeyOpts): Promise<void> => {
        await axios.post<Envelope<{ published: HomeLayoutResponse }>>(`/gluon/mobile-home-layout/rollback`, { ...keyBody(scope, opts), versionIndex })
    },
}
